import React, {
  useRef,
  useCallback,
  useState,
  useEffect,
  ChangeEvent,
} from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import Grid from '@material-ui/core/Grid';

import getValidationErrors from '../../../../../../../../../utils/getValidationErrors';
import { useToast } from '../../../../../../../../../hooks/toast';
import { useRegisterAdded } from '../../../../../../../../../hooks/registerAdded';
import api from '../../../../../../../../../services/api';

import InputLogged from '../../../../../../../../../components/InputLogged';
import {
  TabWrapper,
  LabelWrapper,
  OptionsWrapper,
  Quantidade,
  ButtonAction,
} from '../../../../../drawer_styles';
import { SubmitButtonWrapper } from '../../../../../../styles';
import material_styles from '../../../../../drawer_styles/material_styles';

interface ComplementoType {
  id: number;
  empresa_id: number;
  descricao: string;
  qtd_min: number;
  qtd_max: number;
  complemento_obrigatorio: boolean;
  tipo: number;
  imagem?: File;
  ativo: boolean;
}

interface EditComplementoProps {
  complemento: ComplementoType;
}

const Detalhes: React.FC<EditComplementoProps> = ({ complemento }) => {
  const [qtdMin, setQtdMin] = useState(Math.trunc(complemento.qtd_min));
  const [qtdMax, setQtdMax] = useState(Math.trunc(complemento.qtd_max));
  const [descricao, setDescricao] = useState(complemento.descricao);
  const [isObrigatorio, setIsObrigatorio] = useState<boolean>(
    complemento.complemento_obrigatorio
  );
  const [errorQtd, setErrorQtd] = useState(false);

  const { addToast } = useToast();
  const {
    handleSetComplementoUpdated,
    handleSetComplementoDrawerClosed,
  } = useRegisterAdded()

  const formRef = useRef<FormHandles>(null);
  const classes = material_styles();

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          descricao: Yup.string().required('O nome do complemento é obrigatório.'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.put(`/complemento/${complemento.id}`, {
          descricao: data.descricao,
          qtd_min: qtdMin,
          qtd_max: qtdMax,
          complemento_obrigatorio: isObrigatorio,
        });

        addToast({
          type: 'success',
          title: 'Dados atualizados.',
        });

        handleSetComplementoUpdated(true);
        handleSetComplementoDrawerClosed(true);
      } catch(err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          addToast({
            type: 'error',
            title: 'Preencha os dados corretamente pra continuar',
          });
        } else {
          const { message } = err.response.data;

          addToast({
            type: 'error',
            title: message,
          });
        }
      }
    },
    [
      complemento,
      addToast,
      qtdMin,
      qtdMax,
      isObrigatorio,
      handleSetComplementoUpdated
    ],
  );

  const handleChangeObrigatoriedade = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === 'yes') {
        setIsObrigatorio(true);
        setQtdMin(1);
        return;
      }

      setIsObrigatorio(false);
      setQtdMin(0);
    },
    [],
  );

  const handleSetQtd = useCallback(
    (input: string, operacao: string) => {
      if (operacao === 'increment') {
        if (!isObrigatorio && input === 'qtdMin') {
          setQtdMin(0);
          return;
        }

        input === 'qtdMin' ? setQtdMin(qtdMin + 1) : setQtdMax(qtdMax + 1);
      } else if (operacao === 'decrement') {
        if (input === 'qtdMin') {
          if (!isObrigatorio) {
            setQtdMin(0);
            return;
          }

          if (qtdMin - 1 <= 0) return;

          setQtdMin(qtdMin - 1);
        }

        if (input === 'qtdMax') {
          if (qtdMax - 1 <= 0) return;
          setQtdMax(qtdMax - 1);
        }
      }
    },
    [qtdMin, qtdMax, isObrigatorio],
  );

  return (
    <TabWrapper>
      <Form ref={formRef} onSubmit={handleSubmit} initialData={{ descricao }}>
        <Grid container spacing={3}>
          <Grid item xs={12} className={classes.noPaddingBot}>
            <LabelWrapper>
              <span>Nome do grupo de complementos:</span>
            </LabelWrapper>
            <InputLogged name="descricao" placeholder="Nome do produto" />
          </Grid>

          <Grid item xs={12} className={classes.noPaddingBot}>
            <OptionsWrapper>
              <div className="title_wrapper">
                <h1>Obrigatoriedade</h1>
                <span>
                  Indique se essa categoria de complementos é necessária para
                  todos os pedidos
                </span>
              </div>

              <div className="options_wrapper">
                <div>
                  <label htmlFor="no_have">
                    <input
                      checked={!isObrigatorio}
                      type="radio"
                      id="optional"
                      name="is_optional"
                      value="no"
                      onChange={handleChangeObrigatoriedade}
                    />
                    Opcional
                  </label>
                  <span>O cliente pode ou não selecionar esse item.</span>
                </div>

                <div>
                  <label htmlFor="have">
                    <input
                      checked={isObrigatorio}
                      type="radio"
                      id="no_optional"
                      name="is_optional"
                      value="yes"
                      onChange={handleChangeObrigatoriedade}
                    />
                    Obrigatório
                  </label>
                  <span>
                    O cliente deve selecionar pelo menos um complemento.
                  </span>
                </div>
              </div>
            </OptionsWrapper>
          </Grid>

          <Grid item xs={12}>
            <Quantidade isErrored={errorQtd}>
              <h1>Quantidade</h1>
              <span className="subtitle">
                Indique quantos itens podem ser selecionados.
              </span>

              <div className="wrapper">
                <div>
                  <span>Mínimo</span>

                  <div className="input_qtd">
                    <button
                      type="button"
                      onClick={() => handleSetQtd('qtdMin', 'decrement')}
                    >
                      -
                    </button>
                    <input type="number" min="1" value={qtdMin} readOnly />
                    <button
                      type="button"
                      onClick={() => handleSetQtd('qtdMin', 'increment')}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div>
                  <span>Máximo</span>

                  <div className="input_qtd">
                    <button
                      type="button"
                      onClick={() => handleSetQtd('qtdMax', 'decrement')}
                    >
                      -
                    </button>
                    <input type="number" min="1" value={qtdMax} readOnly />
                    <button
                      type="button"
                      onClick={() => handleSetQtd('qtdMax', 'increment')}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </Quantidade>
          </Grid>

          <Grid item xs={12} className={classes.noPaddingBot}>
            <SubmitButtonWrapper>
              <ButtonAction
                type="button"
                className="cancel"
                onClick={() => handleSetComplementoDrawerClosed(true)}
              >
                Cancelar
              </ButtonAction>

              <ButtonAction type="submit" className="submit">
                Salvar
              </ButtonAction>
            </SubmitButtonWrapper>
          </Grid>
        </Grid>
      </Form>
    </TabWrapper>
  );
};

export default Detalhes;
